
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Users } from "lucide-react";
import { UniversalUserCard } from "@/components/common/UniversalUserCard";
import { useUserSearch } from "@/hooks/useUserSearch";
import { useRecommendations } from "@/hooks/useRecommendations";
import { useTranslation } from "react-i18next";

export const SearchTab = () => {
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const { searchResults, loading: searchLoading, searchUsers, sendFriendRequest } = useUserSearch();
  const { recommendations, loading: recommendationsLoading } = useRecommendations();
  const { t } = useTranslation('friends');

  const handleUserSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await searchUsers(userSearchQuery);
  };

  const handleSendFriendRequest = async (userId: string) => {
    await sendFriendRequest(userId);
  };

  return (
    <div className="space-y-4">
      <div className="max-w-2xl mx-auto">
        {/* Recommendations Section */}
        {recommendations.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-uplivion-blue" />
              <h2 className="text-xl font-semibold text-uplivion-blue">
                {t('search.recommendedForYou')}
              </h2>
            </div>
            <div className="space-y-3">
              {recommendationsLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : (
                recommendations.map(user => (
                  <div key={user.id} className="relative">
                    <UniversalUserCard 
                      user={user} 
                      variant="search"
                      onSendFriendRequest={handleSendFriendRequest}
                    />
                    <div className="absolute top-2 right-2">
                      <span className="bg-uplivion-lightBlue text-uplivion-blue text-xs px-2 py-1 rounded-full">
                        {user.referral_source === 'referral' ? t('search.invitedYou') : t('search.fromInvitation')}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search Section */}
        <form onSubmit={handleUserSearch} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('search.placeholder')}
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button 
            type="submit" 
            className="bg-uplivion-blue hover:bg-blue-700"
            disabled={searchLoading}
          >
            {searchLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('search.button')
            )}
          </Button>
        </form>
        
        {userSearchQuery && (
          <h2 className="text-xl font-semibold mb-4">
            {t('search.resultsFor', { query: userSearchQuery })}
          </h2>
        )}
        
        <div className="space-y-4">
          {searchLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map(user => (
              <UniversalUserCard 
                key={user.id} 
                user={user} 
                variant="search"
                onSendFriendRequest={handleSendFriendRequest}
              />
            ))
          ) : userSearchQuery ? (
            <Card className="uplivion-card">
              <CardContent className="p-8 text-center">
                <p className="text-gray-500">{t('search.empty')}</p>
                <p className="text-sm text-gray-400 mt-2">
                  {t('search.emptySubtext')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="uplivion-card">
              <CardContent className="p-8 text-center">
                <p className="text-gray-500">{t('search.noQuery')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
